import { Box, Button, Divider, IconButton, Stack } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMessage } from "../../../../base/MessageProvider";
import { useDispatch, useSelector } from "react-redux";
import { AddCircleOutlined, Check, Delete, Edit } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import ReactTextInput from "../../../../components/ControllerComponent/ReactTextInput";
import ReactSelect from "../../../../components/ControllerComponent/ReactSelect";
import ReactiveDatePickerField from "../../../../components/ControllerComponent/ReactDatePicker";
import validationSchema from "./validation";
import {
  setSelectedRoles,
  removeRollData,
  fetchInitialAuditionData,
  addProjectData,
  generateProject,
  addAiRollData,
  setAutofillDescription,
  updateRollTitle,
  updateJobType,
} from "../../../actions/project";
import bgImage from "../../../../assets/images/ai_loader.gif";
import SelectFieldForMap from "../../../../components/ControllerComponent/SelectFieldForMap";
import RoleTitleEditModal from "../RoleTitleEditModal";
import clsx from "clsx";

const ProjectForm = (props) => {
  const [roleTitleModal, setRoleTitleModal] = useState(false);
  const [titleRole, setTitleRole] = useState({});

  const selectedRoleIndex = useRef(-1);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { open, onClose, navigation, isAi = false } = props;

  const alert = useMessage();
  const dispatch = useDispatch();

  const selectedRecruiter = useSelector(
    (state) => state.recruiterReducer.selectedRecruiter
  );

  const userId = selectedRecruiter?.userId;

  const selectedRoleList =
    useSelector((state) => state.projectReducer.selectedRoleList) ?? [];

  const autofillDescription = useSelector(
    (state) => state.projectReducer.autofillDescription
  );

  const generatedProject = useSelector(
    (state) => state.projectReducer.generatedProject
  );

  const projectCategoryList = useSelector(
    (state) => state.projectCategoryReducer?.projectCategoryList ?? []
  );

  const projectCategoryOptions = useMemo(
    () =>
      projectCategoryList.map((item) => ({ label: item.name, value: item.id })),
    [projectCategoryList]
  );

  const loading = useSelector((state) => state.projectReducer.loading);

  const generatedRef = useRef(generatedProject);

  if (generatedProject !== generatedRef.current) {
    generatedRef.current = generatedProject;
  }

  useEffect(() => {
    const init = async () => {
      if (generatedRef.current && isAi) {
        const generatedProject = generatedRef.current;

        console.log("generatedProject---->>>>", generatedProject);

        if (generatedProject) {
          setValue("name", generatedProject?.data?.name ?? "");
          setValue("description", generatedProject?.data?.description ?? "");
          setValue(
            "projectCategoryId",
            generatedProject?.data?.projectCategory?.id
          );
          const endDate = generatedProject?.data?.endDate
            ? new Date(generatedProject?.data?.endDate)
            : undefined;

          if (endDate && endDate < new Date()) {
            setValue("endDate", undefined);
          } else {
            setValue("endDate", endDate);
          }

          dispatch(
            addAiRollData({
              ...generatedProject?.data?.rolesList,
            })
          );
          dispatch(setAutofillDescription(""));
        }
      }
    };
    init();
  }, [generatedRef.current, isAi]);

  useEffect(() => {
    if (autofillDescription !== "" && open && isAi) {
      dispatch(
        generateProject(
          autofillDescription,
          () => {},
          (error) => {
            alert.showError(
              error?.message ?? "Request Timeout! Please try again!",
              { timeout: 10000 }
            );
            onClose();
          }
        )
      );
    }
  }, [autofillDescription, open, isAi]);

  useEffect(() => {
    dispatch(
      fetchInitialAuditionData(
        () => {},
        (error) => {
          console.log("Error:", error);
        }
      )
    );
  }, []);

  useEffect(() => {
    if (open) {
      clearErrors();
      reset();
      dispatch(setSelectedRoles([]));
      setValue("name", "");
      setValue("description", "");
      setValue("projectCategoryId", "");
      setValue("endDate", null);
    }
  }, [open]);

  const handleAddRole = useCallback(() => {
    // if (!currentProject) {
    //   alert.showError("Please select project type first");
    //   return;
    // }
    navigation.navigate("RoleForm");
  }, []);

  const onSuccessCallBack = (res) => {
    alert.showSuccess(res?.message ?? "Successfully added!!");
    onClose();
  };

  const onsubmit = async (isdraft, data) => {
    // showLoadingOverlay("Adding project");

    const roleDetailsMissing = selectedRoleList.some((role, index) => {
      const isValid = isValidate(role);
      if (!isValid) {
        return true; // Stops the loop
      }
      return false;
    });

    if (roleDetailsMissing) {
      alert.showError("Role details are missing", { timeout: 10000 });
      return;
    }

    if (!userId) {
      alert.showError("Select Recruiter to submit project", { timeout: 10000 });
      return;
    }

    const updatedData = {
      ...data,
      userId: userId,
      isdraft,
      rolesList: selectedRoleList,
    };
    dispatch(
      addProjectData(updatedData, onSuccessCallBack, (error) => {
        const errorMessage =
          error?.message === "Project User ID Required"
            ? "Select recruiter to submit project"
            : error?.message ?? "Something went wrong!!";

        alert.showError(errorMessage, { timeout: 10000 });
      })
    );
    // hideLoadingOverlay();
  };

  const handleRemoveRole = (index) => {
    dispatch(removeRollData(index, selectedRoleList, () => {}));
  };

  const onRemoveRole = (index) => {
    alert.showConfirmation({
      message: `Are you sure you want to remove role?`,
      button1Title: "Cancel",
      button2Title: "Remove",
      onPressBtn1: () => {},
      onPressBtn2: handleRemoveRole?.bind(null, index),
    });
  };

  const onEditRole = (data, index, event) => {
    console.log(event);
    navigation.navigate("RoleForm", {
      metadata: data,
      index,
    });
  };

  const onError = (errors) => {
    // Extract field names from errors
    const missingFields = Object.keys(errors);

    // Determine message based on number of missing fields
    if (missingFields.length > 1) {
      alert.showError("One or more details are missing", { timeout: 10000 });
    } else if (missingFields.length === 1) {
      const fieldName = missingFields[0];
      let missingFieldName = "";
      if (fieldName === "name") {
        missingFieldName = "Project folder name";
      } else if (fieldName === "projectType") {
        missingFieldName = "Project type";
      } else if (fieldName === "endDate") {
        missingFieldName = "Expiry Date";
      }

      alert.showError(`${missingFieldName} is required`, { timeout: 10000 });
    }
  };

  if (loading) {
    return (
      <Box
        className="h-full w-full flex items-center justify-center pb-[800px]"
        sx={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "80%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
    );
  }

  const handleEditRoleTitle = async (value) => {
    const { data } = value;
    dispatch(
      updateRollTitle(
        {
          title: data.roleTitle,
          index: selectedRoleIndex.current,
        },
        selectedRoleList
      )
    );
    setRoleTitleModal(false);
  };

  const handleModalOpen = (item) => {
    setTitleRole(item);
    setRoleTitleModal(true);
  };

  const isValidate = (role) => {
    if (!role?.profileCategory.id?.trim()) {
      return false;
    } else if (!role?.roleType?.id.trim() && !role?.role) {
      return false;
    } else if (!role?.gender) {
      return false;
    } else if (!role?.characterDescription.trim()) {
      return false;
    }
    return true;
  };

  return (
    <Box className="w-full flex flex-col justify-between pb-7 overflow-y-auto">
      <div>
        <div className="flex items-center py-2 px-4">
          <h2 className="w-full text-center font-['Poppins-Semibold'] leading-[30px] text-[14px] ">
            Creating Your Project
          </h2>
        </div>
        <Divider />
        <Stack px={3} py={4} spacing={3}>
          <ReactTextInput
            hookFormControl={control}
            label="Project Folder Name*"
            varName="name"
            key={"name"}
            maxLength={50}
            errorMessage={errors?.name?.message}
          />
          <ReactTextInput
            hookFormControl={control}
            label={"Describe Your Project"}
            varName="description"
            key={"description"}
            rows={3}
            maxLength={600}
            isMultiLine
            errorMessage={errors?.description?.message}
          />
          <ReactSelect
            hookFormControl={control}
            label="Project Type*"
            varName="projectCategoryId"
            key={"projectCategoryId"}
            options={projectCategoryOptions}
            errorMessage={errors?.projectCategoryId?.message}
          />
          <ReactiveDatePickerField
            hookFormControl={control}
            label={"End Date*"}
            varName={"endDate"}
            errorMessage={errors?.endDate?.message}
            minDate={new Date()}
          />
          <div
            onClick={handleAddRole}
            className="flex justify-between items-center rounded-lg bg-gradient-to-b from-gray-300 to-gray-100 p-4 active:from-gray-500 active:to-gray-300 select-none cursor-pointer"
          >
            <h1 className="font-['Poppins-Medium'] text-[17px]">Add Roles</h1>
            <AddCircleOutlined className="text-[#00684E]" />
          </div>
        </Stack>
        {selectedRoleList?.length > 0 ? (
          <>
            <Divider />
            {selectedRoleList?.map((item, index) => {
              const handleOnChange = (value, roleIndex) => {
                selectedRoleIndex.current = roleIndex;
                dispatch(
                  updateJobType(
                    {
                      jobType: value,
                      index: roleIndex,
                    },
                    selectedRoleList
                  )
                );
              };

              const isValidRole = isValidate(item);

              return (
                <div className="flex flex-col justify-between items-center p-2 space-y-4 bg-gray-200 rounded-lg m-2">
                  <div
                    key={`role-${index}`}
                    className={clsx(
                      "w-full items-center rounded-lg bg-white select-none overflow-hidden border",
                      isValidRole ? "border-white" : "border-[#FF1060]"
                    )}
                  >
                    <div onClick={onEditRole?.bind(this, item, index)}>
                      <div className="w-full flex justify-between items-center">
                        <div className="font-['Poppins-Medium'] text-[15px] px-2 py-4 cursor-pointer active:bg-gray-300 leading-tight">
                          {item?.title}
                        </div>

                        <div className="flex items-center">
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              selectedRoleIndex.current = index;
                              handleModalOpen(item);
                            }}
                            sx={{ my: 1 }}
                          >
                            <Edit className="text-green-600 text-[20px]" />
                          </IconButton>
                          <IconButton
                            onClick={(event) => {
                              event.stopPropagation();
                              onRemoveRole?.(index);
                            }}
                            sx={{ my: 1 }}
                          >
                            <Delete className="text-red-600 text-[20px]" />
                          </IconButton>
                        </div>
                      </div>
                      <div className="font-['Poppins-Medium'] text-[12px] px-2 -mt-4 mb-4 cursor-pointer active:bg-gray-300">
                        {item.gender}, {item?.minAge}-{item?.maxAge}
                      </div>
                    </div>

                    {item.selfTestCheck && (
                      <div>
                        <Divider />
                        <div className="w-full flex justify-between items-center py-4">
                          <SelectFieldForMap
                            data={item.jobType}
                            onChange={(value) => handleOnChange(value, index)}
                          />
                          <IconButton
                            sx={{
                              m: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5, // Adjust spacing between icon and text
                            }}
                          >
                            <p className="font-['Poppins-Medium'] text-[13px] text-green-600">
                              Selftest Submitted
                            </p>
                            <Check
                              className="text-green-600"
                              fontSize="small"
                            />
                          </IconButton>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <Divider />
            <div className="flex justify-between items-center gap-4 py-5 px-5">
              {/* <Button
                onClick={handleSubmit(onsubmit?.bind(this, true))}
                variant="contained"
                className="w-full bg-gray-300 font-['Poppins-Medium'] normal-case text-[14px] text-black"
              >
                DRAFT
              </Button> */}
              <Button
                onClick={handleSubmit(onsubmit?.bind(this, false), onError)}
                variant="contained"
                className="w-full font-['Poppins-Medium'] normal-case text-[14px] text-white"
              >
                SUBMIT
              </Button>
            </div>
          </>
        ) : (
          <div className="flex -mt-8">
            <h2 className="w-full text-center font-['Poppins-Semibold'] leading-[30px] text-[12px]">
              *Add atleast 1 role to submit your requirement.
            </h2>
          </div>
        )}
      </div>
      <RoleTitleEditModal
        open={roleTitleModal}
        onClose={setRoleTitleModal?.bind(this, false)}
        onSubmit={handleEditRoleTitle}
        role={titleRole}
      />
    </Box>
  );
};

export default ProjectForm;
